/**
 * @fileoverview GitHub User Metrics — Per-User Copilot Usage Dashboard
 * @description Displays individual per-user Copilot usage data from the
 *              users-28-day report API. No org-level metrics here.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Collapse,
  Stack,
} from '@mui/material';
import Spinner from '@/components/common/Spinner/Spinner';
import {
  Users,
  Activity,
  Zap,
  Code,
  RefreshCw,
  AlertCircle,
  Monitor,
  MessageSquare,
  CheckCircle,
  BarChart3,
  ChevronDown,
} from 'lucide-react';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import UserBarChart from '../components/UserBarChart';
import MonthSelector from '../components/MonthSelector';
import { githubUserMetricsApi } from '@/services/api/githubUserMetrics';
import {
  getSeatStatusBreakdown,
  getLeastActiveCopilotUsers,
  getLocAddedByUser,
  getAcceptanceRateByUser,
  getActiveDaysByUser,
  getChatInteractionsByUser,
  getIdeUsageAcrossUsers,
  getLanguageUsageAcrossUsers,
  getFeatureUsageAcrossUsers,
  getLastEditorByUser,
  getSummaryStats,
} from '../utils';
import {appInsights} from '@/telemetry';
import './GitHubUserMetrics.css';

// ── Helpers ──────────────────────────────────────────────────

const formatCompactNumber = (value) => {
  const n = Number(value) || 0;
  if (Math.abs(n) >= 1000000) {
    return `${(n / 1000000).toFixed(n % 1000000 === 0 ? 0 : 1)}M`;
  }
  if (Math.abs(n) >= 1000) {
    return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}K`;
  }
  return String(n);
};

// ── Helper sub-components ───────────────────────────────────

const StatCard = ({ icon: Icon, label, value, color, bgColor, badge }) => (
  <div className="gum-stat-card">
    <div className="gum-stat-icon" style={{ '--stat-color': color, '--stat-bg': bgColor }}>
      <Icon size={22} />
    </div>
    <div className="gum-stat-content">
      <div className="gum-stat-label">
        {label}
        {badge && <span className="gum-chart-badge" style={{ marginLeft: 6, fontSize: '0.65rem' }}>{badge}</span>}
      </div>
      <div
        className="gum-stat-value"
        title={typeof value === 'number' ? value.toLocaleString() : String(value)}
      >
        {typeof value === 'number' ? formatCompactNumber(value) : value}
      </div>
    </div>
  </div>
);

const ChartCard = ({ title, icon: Icon, badge, children }) => (
  <div className={`gum-chart-card${badge ? ' gum-chart-card--universal' : ''}`}>
    <div className="gum-chart-header">
      <h3 className="gum-chart-title">
        {Icon && <Icon size={18} />}
        {title}
      </h3>
      {badge && <span className="gum-chart-badge">{badge}</span>}
    </div>
    <div className="gum-chart-body">{children}</div>
  </div>
);

const CollapsibleChartRow = ({ title, defaultExpanded = false, children }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <div className={`gum-collapsible-row ${expanded ? 'expanded' : ''}`}>
      <div
        className="gum-collapsible-row-header"
        onClick={() => setExpanded(!expanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpanded(!expanded); }}
        aria-expanded={expanded}
      >
        <span className="gum-collapsible-row-title">{title}</span>
        <ChevronDown size={18} className={`gum-chart-chevron ${expanded ? 'expanded' : ''}`} />
      </div>
      <Collapse in={expanded}>
        <div className="gum-chart-grid">
          {children}
        </div>
      </Collapse>
    </div>
  );
};

const ChartPlaceholder = ({ message = 'No data available' }) => (
  <div className="gum-chart-placeholder">
    <AlertCircle size={28} />
    <span>{message}</span>
  </div>
);

const SkeletonStat = () => <div className="gum-skeleton gum-skeleton-stat" />;
const SkeletonChart = () => <div className="gum-skeleton gum-skeleton-chart" />;

// ── Color palette for charts ────────────────────────────────

const CHART_COLORS = [
  '#01426A', '#0097A9', '#00AF66', '#FFB81C', '#E31837',
  '#6366f1', '#8b5cf6', '#ec4899', '#f97316', '#14b8a6',
  '#3b82f6', '#a855f7', '#ef4444', '#22c55e', '#eab308',
];

const SEAT_COLORS = {
  active: '#00AF66',
  inactive: '#FFB81C',
  no_seat: '#E31837',
};

// ── Main Page Component ─────────────────────────────────────

export const GitHubUserMetrics = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [metricsData, setMetricsData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);

  const loadMetrics = useCallback(async (force = false, month = selectedMonth) => {
    if (force) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      const resp = await githubUserMetricsApi.getAllMetrics(force, month);
      const payload = resp?.data ?? resp;
      setMetricsData(payload);
      // Set initial selected month from API response if not already set
      if (!month && payload?.month) {
        setSelectedMonth(payload.month);
      }
    } catch (err) {
      setError(err.message || 'Failed to load Copilot user metrics');
      appInsights.trackException({ 
        exception: err, 
        properties: { 
          source: 'GitHubUserMetrics', force, month 
        } 
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    loadMetrics();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleMonthChange = useCallback((month) => {
    setSelectedMonth(month);
    loadMetrics(false, month);
  }, [loadMetrics]);

  const users = metricsData?.users ?? [];

  // ── Transformed chart data ──────────────────────────────

  const summary = useMemo(() => (users.length > 0 ? getSummaryStats(users) : null), [users]);
  const seatStatus = useMemo(() => getSeatStatusBreakdown(users), [users]);
  const leastActive = useMemo(() => getLeastActiveCopilotUsers(users), [users]);
  const locByUser = useMemo(() => getLocAddedByUser(users, 15), [users]);
  const acceptanceByUser = useMemo(() => getAcceptanceRateByUser(users, 15), [users]);
  const activeDays = useMemo(() => getActiveDaysByUser(users), [users]);
  const chatByUser = useMemo(() => getChatInteractionsByUser(users, 15), [users]);
  const ideUsage = useMemo(() => getIdeUsageAcrossUsers(users), [users]);
  const languageUsage = useMemo(() => getLanguageUsageAcrossUsers(users), [users]);
  const featureUsage = useMemo(() => getFeatureUsageAcrossUsers(users), [users]);
  const lastEditors = useMemo(() => getLastEditorByUser(users), [users]);

  // ── Loading state ───────────────────────────────────────

  if (loading) {
    return (
      <Box className="gum-page" sx={{ p: 3 }}>
        <Typography variant="h4" fontWeight={700} color="primary.main" mb={3}>
          <Zap size={30} style={{ verticalAlign: 'middle', marginRight: 10 }} />
          Copilot User Metrics
        </Typography>
        <div className="gum-stats-row">
          {[1, 2, 3, 4, 5, 6].map((n) => <SkeletonStat key={n} />)}
        </div>
        <div className="gum-chart-grid">
          {[1, 2, 3, 4].map((n) => <SkeletonChart key={n} />)}
        </div>
      </Box>
    );
  }

  // ── Error state ─────────────────────────────────────────

  if (error && !metricsData) {
    return (
      <Box className="gum-page" sx={{ p: 3 }}>
        <Typography variant="h4" fontWeight={700} color="primary.main" mb={3}>
          <Zap size={30} style={{ verticalAlign: 'middle', marginRight: 10 }} />
          Copilot User Metrics
        </Typography>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // ── Empty state ─────────────────────────────────────────

  if (users.length === 0) {
    return (
      <Box className="gum-page" sx={{ p: 3 }}>
        <Typography variant="h4" fontWeight={700} color="primary.main" mb={3}>
          <Zap size={30} style={{ verticalAlign: 'middle', marginRight: 10 }} />
          Copilot User Metrics
        </Typography>
        <div className="gum-empty">
          <Users size={48} style={{ color: '#9ca3af', marginBottom: 12 }} />
          <Typography variant="h6" color="text.secondary">No tracked users yet</Typography>
          <Typography variant="body2" color="text.secondary">
            Add GitHub usernames via the API to start tracking Copilot usage.
          </Typography>
        </div>
      </Box>
    );
  }

  // ── Main render ─────────────────────────────────────────

  return (
    <Box className="gum-page" sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary.main">
            <Zap size={30} style={{ verticalAlign: 'middle', marginRight: 10 }} />
            Copilot User Metrics
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Per-user Copilot usage for {summary?.totalUsers} tracked users in {metricsData?.org || 'Alaska-ITS'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <MonthSelector
            months={metricsData?.months || []}
            selected={selectedMonth}
            onChange={handleMonthChange}
            disabled={loading || refreshing}
          />
          <Button
            variant="outlined"
            startIcon={refreshing ? <Spinner size={16} /> : <RefreshCw size={16} />}
            onClick={() => loadMetrics(true, selectedMonth)}
            disabled={refreshing}
            size="small"
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Stack>
      </Stack>

      {/* Errors from partial failures */}
      <Collapse in={!!error}>
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      </Collapse>
      {metricsData?.errors?.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {metricsData.errors.length} user(s) had fetch errors and were skipped.
        </Alert>
      )}

      {/* ── Usage Overview ───────────────────────────────── */}

      {/* Summary Stats */}
      {summary && (
        <div className="gum-stats-row">
          <StatCard icon={Users} label="Tracked Users" value={metricsData?.total_tracked ?? summary.totalUsers}
            color="#01426A" bgColor="#eff6ff" />
          <StatCard icon={CheckCircle} label="With Copilot Seat" value={summary.withSeat}
            color="#00AF66" bgColor="#ecfdf5" badge="Live" />
          <StatCard icon={Activity} label="Active (10d)" value={summary.activeRecent}
            color="#0097A9" bgColor="#ecfeff" badge="Live" />
          <StatCard icon={Code} label="Total LOC Added" value={summary.totalLocAdded}
            color="#6366f1" bgColor="#eef2ff" />
          <StatCard icon={BarChart3} label="Completions Accepted" value={summary.totalAccepted}
            color="#FFB81C" bgColor="#fefce8" />
          <StatCard icon={MessageSquare} label="Chat Interactions" value={summary.totalChatInteractions}
            color="#ec4899" bgColor="#fdf2f8" />
        </div>
      )}

      {/* Chart Row 1: LOC Added by User + Acceptance Rate by User */}
      <CollapsibleChartRow title="Lines of Code & Acceptance Rate" defaultExpanded={true}>
        <ChartCard title="Lines of Code Added via Copilot (per User)" icon={Code}>
          <UserBarChart
            data={locByUser}
            bars={[
              { key: 'locAdded', label: 'LOC Added', color: '#00AF66' },
            ]}
            maxItems={15}
            emptyMessage="No LOC data available"
          />
        </ChartCard>

        <ChartCard title="Acceptance Rate (per User)" icon={BarChart3}>
          <UserBarChart
            data={acceptanceByUser}
            bars={[{ key: 'rate', label: 'Acceptance %', color: '#6366f1' }]}
            valueFormatter={(v) => `${v}%`}
            maxItems={15}
            emptyMessage="No acceptance data available"
          />
        </ChartCard>
      </CollapsibleChartRow>

      {/* Chart Row 2: Active Days by User + Chat Interactions by User */}
      <CollapsibleChartRow title="Active Days & Chat Interactions">
        <ChartCard title={`Active Days (per User)`} icon={Activity}>
          <UserBarChart
            data={activeDays}
            bars={[{ key: 'activeDays', label: 'Active Days', color: '#01426A' }]}
            valueFormatter={(v) => `${v}d`}
            maxItems={15}
            emptyMessage="No activity data available"
          />
        </ChartCard>

        <ChartCard title="Chat Interactions (per User)" icon={MessageSquare}>
          <UserBarChart
            data={chatByUser}
            bars={[{ key: 'interactions', label: 'Chat Interactions', color: '#ec4899' }]}
            maxItems={15}
            emptyMessage="No chat data available"
          />
        </ChartCard>
      </CollapsibleChartRow>

      {/* Chart Row 3: Seat Status (pie) + Least Active Users */}
      <CollapsibleChartRow title="Seat Status & Inactive Users">
        <ChartCard title="Copilot Seat Status" icon={Users} badge="All Time">
          {seatStatus.length > 0 ? (
            <PieChart
              series={[{
                data: seatStatus.map((d) => ({
                  ...d,
                  color: SEAT_COLORS[d.id] || '#6366f1',
                })),
                innerRadius: 50,
                outerRadius: 120,
                paddingAngle: 3,
                cornerRadius: 4,
              }]}
              height={300}
              slotProps={{
                legend: {
                  direction: 'column',
                  position: { vertical: 'middle', horizontal: 'right' },
                  itemMarkWidth: 10,
                  itemMarkHeight: 10,
                  labelStyle: { fontSize: 12 },
                },
              }}
            />
          ) : (
            <ChartPlaceholder />
          )}
        </ChartCard>

        <ChartCard title="Inactive Copilot Users" icon={AlertCircle} badge="All Time">
          <UserBarChart
            data={leastActive.map((d) => ({ ...d, days: d.days < 0 ? 999 : d.days }))}
            bars={[{ key: 'days', label: 'Days Since Active', color: '#E31837' }]}
            valueFormatter={(v) => (v >= 999 ? 'Never' : `${v}d ago`)}
            emptyMessage="No inactive users found"
          />
        </ChartCard>
      </CollapsibleChartRow>

      {/* Chart Row 4: IDE Usage (bar) + Language Usage (pie) */}
      <CollapsibleChartRow title="IDE & Language Usage">
        <ChartCard title="IDE Usage (across Users)" icon={Monitor}>
          {ideUsage.length > 0 ? (
            <BarChart
              dataset={ideUsage}
              xAxis={[{ scaleType: 'band', dataKey: 'editor', tickLabelStyle: { fontSize: 11 } }]}
              series={[{ dataKey: 'activity', label: 'Total Activity', color: '#01426A' }]}
              height={300}
              margin={{ bottom: 60 }}
              slotProps={{ legend: { hidden: true } }}
            />
          ) : (
            <ChartPlaceholder message="No IDE data available" />
          )}
        </ChartCard>

        <ChartCard title="Language Usage (across Users)" icon={Code}>
          {languageUsage.length > 0 ? (
            <PieChart
              series={[{
                data: languageUsage.map((d, i) => ({
                  ...d,
                  color: CHART_COLORS[i % CHART_COLORS.length],
                })),
                innerRadius: 50,
                outerRadius: 120,
                paddingAngle: 2,
                cornerRadius: 4,
              }]}
              height={300}
              slotProps={{
                legend: {
                  direction: 'column',
                  position: { vertical: 'middle', horizontal: 'right' },
                  itemMarkWidth: 10,
                  itemMarkHeight: 10,
                  labelStyle: { fontSize: 11 },
                },
              }}
            />
          ) : (
            <ChartPlaceholder message="No language data available" />
          )}
        </ChartCard>
      </CollapsibleChartRow>

      {/* Chart Row 5: Feature Usage (pie) + Last Used Editor (bar) */}
      <CollapsibleChartRow title="Feature Usage & Editor Preference">
        <ChartCard title="Feature Usage (across Users)" icon={Zap}>
          {featureUsage.length > 0 ? (
            <PieChart
              series={[{
                data: featureUsage.map((d, i) => ({
                  ...d,
                  color: CHART_COLORS[i % CHART_COLORS.length],
                })),
                innerRadius: 50,
                outerRadius: 120,
                paddingAngle: 3,
                cornerRadius: 4,
              }]}
              height={300}
              slotProps={{
                legend: {
                  direction: 'column',
                  position: { vertical: 'middle', horizontal: 'right' },
                  itemMarkWidth: 10,
                  itemMarkHeight: 10,
                  labelStyle: { fontSize: 12 },
                },
              }}
            />
          ) : (
            <ChartPlaceholder message="No feature data available" />
          )}
        </ChartCard>

        <ChartCard title="Last Used Editor (per User)" icon={Monitor} badge="All Time">
          {lastEditors.length > 0 ? (
            <BarChart
              dataset={lastEditors}
              xAxis={[{ scaleType: 'band', dataKey: 'editor', tickLabelStyle: { fontSize: 11 } }]}
              series={[{ dataKey: 'count', label: 'Users', color: '#8b5cf6' }]}
              height={300}
              margin={{ bottom: 60 }}
              slotProps={{ legend: { hidden: true } }}
            />
          ) : (
            <ChartPlaceholder message="No editor data available" />
          )}
        </ChartCard>
      </CollapsibleChartRow>

    </Box>
  );
};

export default GitHubUserMetrics;
